/**
 * Connection Storage Service
 *
 * Handles storing and retrieving Connection data.
 * All functions return Promises for asynchronous API behavior.
 *
 * @module connectionStorage
 */

import {
  getItem,
  setItem,
  removeItem,
  CONNECTION_METADATA_PREFIX,
  RECEIVED_ANON_DATA_PREFIX,
  RECEIVED_PROFILE_DATA_PREFIX,
  PROFILE_NOTES_PREFIX,
  ANON_NOTES_PREFIX,
  CHAT_MESSAGES_PREFIX,
  MANUAL_CONTACT_DATA_PREFIX,
  getManualContactData,
  getAllManualContactIds,
  deleteManualContactData,
} from './userDataStorage';
import { supabase } from '../supabase';

// Connection storage keys
const CONNECTIONS_KEY = 'royale_connections'; // Legacy key - no longer used for primary storage
const LAST_CONNECTION_TIMESTAMP_KEY = 'lastConnectionTimestamp';

/**
 * Get all connections by scanning localStorage for individual data items
 * This is the new primary method for getting connections that avoids the quota issue
 * Includes both exchange connections (via metadata) and manual contacts
 * @returns {Promise<Array>} Array of connections built from individual data items
 */
export const getConnectionsFromIndividualData = async () => {
  try {
    // Get all keys from localStorage
    const allKeys = Object.keys(localStorage);

    // Filter keys by prefix to find all connection-related data
    const metadataKeys = allKeys.filter((key) => key.startsWith(CONNECTION_METADATA_PREFIX));

    // Build connection objects from exchange connections (have metadata)
    const exchangeConnectionPromises = metadataKeys.map(async (metadataKey) => {
      const chatRoomId = metadataKey.replace(CONNECTION_METADATA_PREFIX, '');

      try {
        // Get all data components for this chatRoomId
        const connectionMetadata = await getItem(metadataKey);
        const profileDataKey = `${RECEIVED_PROFILE_DATA_PREFIX}${chatRoomId}`;
        const anonDataKey = `${RECEIVED_ANON_DATA_PREFIX}${chatRoomId}`;
        const profileNotesKey = `${PROFILE_NOTES_PREFIX}${chatRoomId}`;
        const anonNotesKey = `${ANON_NOTES_PREFIX}${chatRoomId}`;

        const profileData = (await getItem(profileDataKey)) || {};
        const anonData = (await getItem(anonDataKey)) || {};
        const profileNotes = (await getItem(profileNotesKey)) || '';
        const anonNotes = (await getItem(anonNotesKey)) || '';
        const chatMessagesKey = `${CHAT_MESSAGES_PREFIX}${chatRoomId}`;
        const chatMessages = (await getItem(chatMessagesKey)) || [];
        const displayName = localStorage.getItem(`display_name_${chatRoomId}`) || null;

        // Also check for manual contact data (for manual contacts that also have metadata)
        const manualContactData = await getManualContactData(chatRoomId);

        // Build a complete connection record
        return {
          id: chatRoomId,
          chatRoomId,
          profileData,
          anonData,
          displayName,
          manualContactData: manualContactData || null,
          connectionMetadata,
          chatMessages,
          notes: profileNotes || anonNotes, // Use whichever notes exist
          createdAt:
            connectionMetadata?.createdAt ||
            connectionMetadata?.lastUpdated ||
            new Date().toISOString(),
        };
      } catch (error) {
        console.error(`[STORAGE] Error building connection for ${chatRoomId}:`, error);
        return null;
      }
    });

    // Also get manual contacts that might not have metadata yet
    // (e.g., manual contacts created before metadata was saved)
    const manualContactIds = await getAllManualContactIds();

    // Filter out manual contacts that already have metadata (already included above)
    const metadataChatRoomIds = new Set(
      metadataKeys.map((key) => key.replace(CONNECTION_METADATA_PREFIX, ''))
    );
    const manualOnlyIds = manualContactIds.filter((id) => !metadataChatRoomIds.has(id));

    // Build connection objects for manual-only contacts
    const manualConnectionPromises = manualOnlyIds.map(async (chatRoomId) => {
      try {
        const manualContactData = await getManualContactData(chatRoomId);
        if (!manualContactData) return null;

        const profileNotesKey = `${PROFILE_NOTES_PREFIX}${chatRoomId}`;
        const profileNotes = (await getItem(profileNotesKey)) || '';

        // Build a connection record for manual contact
        return {
          id: chatRoomId,
          chatRoomId,
          profileData: {},
          anonData: {},
          manualContactData,
          connectionMetadata: null, // Will be populated when saved
          chatMessages: [],
          notes: profileNotes,
          createdAt: new Date().toISOString(),
        };
      } catch (error) {
        console.error(`[STORAGE] Error building manual connection for ${chatRoomId}:`, error);
        return null;
      }
    });

    // Wait for all promises to resolve
    const exchangeConnections = (await Promise.all(exchangeConnectionPromises)).filter(Boolean);
    const manualConnections = (await Promise.all(manualConnectionPromises)).filter(Boolean);

    // Combine both types
    const connections = [...exchangeConnections, ...manualConnections];

    console.log(
      `[STORAGE] Built ${connections.length} connections (${exchangeConnections.length} exchange, ${manualConnections.length} manual-only)`
    );
    return connections;
  } catch (error) {
    console.error('[STORAGE] Error building connections from individual data:', error);
    return [];
  }
};

/**
 * Get all connections from storage
 * @returns {Promise<Array>} Array of connections or empty array if none found
 * @deprecated Use getConnectionsFromIndividualData instead
 */
export const getConnections = async () => {
  // Forward to the new implementation
  return getConnectionsFromIndividualData();
};

/**
 * How many connections this device has — the NUMBER only.
 *
 * One keyspace pass, zero parses. getConnections() hydrates every connection
 * (~8 getItem calls each, including a full JSON.parse of every stored
 * chat_messages_* array) and callers that only wanted `.length` were paying
 * all of it — on the exchange hot path, at the moment a share completes, with
 * peer realtime traffic in flight (/simplify efficiency pass, 2026-08-17).
 *
 * Counts exchange connections + manual contacts, matching what
 * getConnectionsFromIndividualData returns.
 *
 * @returns {Promise<number>}
 */
export const getConnectionCount = async () => {
  try {
    const metadataIds = new Set(
      Object.keys(localStorage)
        .filter((key) => key.startsWith(CONNECTION_METADATA_PREFIX))
        .map((key) => key.replace(CONNECTION_METADATA_PREFIX, ''))
    );

    // Manual-ONLY ids, deduped against metadata exactly as the hydrating path
    // does — a manual contact that also has metadata is one connection, not two.
    const manualIds = (await getAllManualContactIds()) || [];
    const manualOnlyIds = manualIds.filter((id) => !metadataIds.has(id));

    // Each manual-only id is confirmed to have data, because the hydrating path
    // drops the ones that don't (`.filter(Boolean)` over a null-returning
    // build). Skipping this check made the count read HIGH for an orphaned id
    // — a wrong badge number. It costs one read per manual-only id, not per
    // connection, so the hot-path win (no per-connection hydration, no parse of
    // any chat_messages array) is untouched.
    const presentManual = await Promise.all(
      manualOnlyIds.map((id) =>
        getManualContactData(id)
          .then(Boolean)
          .catch(() => false)
      )
    );

    return metadataIds.size + presentManual.filter(Boolean).length;
  } catch (error) {
    console.error('[connectionStorage] getConnectionCount failed:', error);
    return 0;
  }
};

/**
 * Get a specific connection by ID (chatRoomId)
 * @param {string} id - The connection ID (chatRoomId)
 * @returns {Promise<Object|null>} The connection or null if not found
 */
export const getConnection = async (id) => {
  try {
    // Get individual data components for this connection
    const metadataKey = `${CONNECTION_METADATA_PREFIX}${id}`;
    const profileDataKey = `${RECEIVED_PROFILE_DATA_PREFIX}${id}`;
    const anonDataKey = `${RECEIVED_ANON_DATA_PREFIX}${id}`;
    const profileNotesKey = `${PROFILE_NOTES_PREFIX}${id}`;
    const anonNotesKey = `${ANON_NOTES_PREFIX}${id}`;

    // Check if the connection metadata exists
    const connectionMetadata = await getItem(metadataKey);
    if (!connectionMetadata) {
      return null;
    }

    // Get the other data components
    const profileData = (await getItem(profileDataKey)) || {};
    const anonData = (await getItem(anonDataKey)) || {};
    const profileNotes = (await getItem(profileNotesKey)) || '';
    const anonNotes = (await getItem(anonNotesKey)) || '';
    const chatMessagesKey = `${CHAT_MESSAGES_PREFIX}${id}`;
    const chatMessages = (await getItem(chatMessagesKey)) || [];

    // Build the connection object
    const connection = {
      id,
      chatRoomId: id,
      profileData,
      anonData,
      connectionMetadata,
      chatMessages,
      notes: profileNotes || anonNotes,
      createdAt:
        connectionMetadata.createdAt || connectionMetadata.lastUpdated || new Date().toISOString(),
    };

    return connection;
  } catch (error) {
    console.error(`[STORAGE] Error getting connection ${id}:`, error);
    return null;
  }
};

/**
 * Save a new connection by saving its individual components
 * @param {Object} connectionRecord - The complete connection record to save
 * @returns {Promise<Object>} The saved connection with generated ID
 */
export const saveConnection = async (connectionRecord) => {
  try {
    // Extract or generate the chatRoomId
    const chatRoomId = connectionRecord.chatRoomId || connectionRecord.id || `conn_${Date.now()}`;

    // Extract the individual components
    const { profileData, anonData, connectionMetadata, notes } = connectionRecord;

    // Set up metadata if it doesn't exist
    const metadata = connectionMetadata || {};
    if (!metadata.createdAt && !metadata.lastUpdated) {
      metadata.createdAt = new Date().toISOString();
      metadata.lastUpdated = new Date().toISOString();
    }

    // Define the keys for each component
    const metadataKey = `${CONNECTION_METADATA_PREFIX}${chatRoomId}`;

    // Save each component individually
    const promises = [];

    // Always save metadata
    promises.push(setItem(metadataKey, metadata));

    // Only save other components if they exist
    if (profileData) {
      const profileDataKey = `${RECEIVED_PROFILE_DATA_PREFIX}${chatRoomId}`;
      promises.push(setItem(profileDataKey, profileData));
    }

    if (anonData) {
      const anonDataKey = `${RECEIVED_ANON_DATA_PREFIX}${chatRoomId}`;
      promises.push(setItem(anonDataKey, anonData));
    }

    if (notes) {
      // Determine if this is for a profile user or anon user based on context
      // Default to profile notes
      const notesKey = `${PROFILE_NOTES_PREFIX}${chatRoomId}`;
      promises.push(setItem(notesKey, notes));
    }

    // Wait for all saves to complete
    await Promise.all(promises);

    // Update the last connection timestamp to help with list refreshing
    await setItem(LAST_CONNECTION_TIMESTAMP_KEY, Date.now().toString());

    // Return the saved connection with the ID
    return {
      ...connectionRecord,
      id: chatRoomId,
      chatRoomId,
    };
  } catch (error) {
    console.error('[STORAGE] Error saving connection:', error);
    throw error;
  }
};

/**
 * Update a connection by updating its individual components
 * @param {string} id - The connection ID (chatRoomId)
 * @param {Object} updates - The fields to update
 * @returns {Promise<Object|null>} The updated connection or null if not found
 */
export const updateConnection = async (id, updates) => {
  try {
    // Get the current connection
    const currentConnection = await getConnection(id);
    if (!currentConnection) {
      return null;
    }

    // Extract the individual components from the updates
    const { profileData, anonData, connectionMetadata, notes } = updates;
    const promises = [];

    // Update each component if it exists in the updates
    if (connectionMetadata) {
      const metadataKey = `${CONNECTION_METADATA_PREFIX}${id}`;
      const updatedMetadata = {
        ...currentConnection.connectionMetadata,
        ...connectionMetadata,
        lastUpdated: new Date().toISOString(),
      };
      promises.push(setItem(metadataKey, updatedMetadata));
    }

    if (profileData) {
      const profileDataKey = `${RECEIVED_PROFILE_DATA_PREFIX}${id}`;
      const updatedProfileData = {
        ...currentConnection.profileData,
        ...profileData,
      };
      promises.push(setItem(profileDataKey, updatedProfileData));
    }

    if (anonData) {
      const anonDataKey = `${RECEIVED_ANON_DATA_PREFIX}${id}`;
      const updatedAnonData = {
        ...currentConnection.anonData,
        ...anonData,
      };
      promises.push(setItem(anonDataKey, updatedAnonData));
    }

    if (notes !== undefined) {
      // Determine if this is for a profile user or anon user based on context
      // Default to profile notes
      const notesKey = `${PROFILE_NOTES_PREFIX}${id}`;
      promises.push(setItem(notesKey, notes));
    }

    // Wait for all updates to complete
    await Promise.all(promises);

    // Update the last connection timestamp to help with list refreshing
    await setItem(LAST_CONNECTION_TIMESTAMP_KEY, Date.now().toString());

    // Get the updated connection
    return getConnection(id);
  } catch (error) {
    console.error(`[STORAGE] Error updating connection ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a connection by removing all its individual components
 * @param {string} id - The connection ID (chatRoomId)
 * @returns {Promise<boolean>} True if deleted, false if not found
 */
export const deleteConnection = async (id) => {
  try {
    // Define the keys for each component
    const metadataKey = `${CONNECTION_METADATA_PREFIX}${id}`;
    const profileDataKey = `${RECEIVED_PROFILE_DATA_PREFIX}${id}`;
    const anonDataKey = `${RECEIVED_ANON_DATA_PREFIX}${id}`;
    const profileNotesKey = `${PROFILE_NOTES_PREFIX}${id}`;
    const anonNotesKey = `${ANON_NOTES_PREFIX}${id}`;

    // Check if the connection exists (via metadata OR manual contact data)
    const connectionMetadata = await getItem(metadataKey);
    const manualContactData = await getManualContactData(id);

    if (!connectionMetadata && !manualContactData) {
      return false;
    }

    // Delete all components (including manual contact data)
    const promises = [
      removeItem(metadataKey),
      removeItem(profileDataKey),
      removeItem(anonDataKey),
      removeItem(profileNotesKey),
      removeItem(anonNotesKey),
      deleteManualContactData(id), // Also delete manual contact data if exists
    ];

    await Promise.all(promises);

    // Per-side delete (CD #15): deleting a connection is one-sided. We must NOT
    // hard-delete the shared `connections` row — that cascades the OTHER person's
    // view/meetings/groups and destroys what they kept. Instead:
    //   1. leave_connection() marks only OUR side (host_left_at/guest_left_at),
    //      so the readers stop delivering our live data to them and theirs to us;
    //      their copy survives, frozen. The peer is not told.
    //   2. Remove only OUR own rows — connection_data (ours) and connection_views
    //      (ours; the view's FK cascade cleans only OUR meetings/groups).
    // Fire-and-forget, matching the optimistic local removal above.
    const profileUserId = localStorage.getItem('profile_user_id');
    if (profileUserId && supabase.from) {
      supabase.rpc('leave_connection', { p_chat_room_id: id }).then(({ error }) => {
        if (error) console.error('[STORAGE] leave_connection failed:', error);
        else console.log('[STORAGE] Left connection (per-side):', id);
      });

      supabase
        .from('connection_data')
        .delete()
        .eq('chat_room_id', id)
        .eq('profile_user_id', profileUserId)
        .then(({ error: cdError }) => {
          if (cdError)
            console.error('[STORAGE] Failed to delete connection_data from DB:', cdError);
        });

      supabase
        .from('connection_views')
        .delete()
        .eq('chat_room_id', id)
        .eq('user_id', profileUserId)
        .then(({ error: cvError }) => {
          if (cvError)
            console.error('[STORAGE] Failed to delete connection_views from DB:', cvError);
        });
    }

    // Update the last connection timestamp to help with list refreshing
    await setItem(LAST_CONNECTION_TIMESTAMP_KEY, Date.now().toString());

    return true;
  } catch (error) {
    console.error(`[STORAGE] Error deleting connection ${id}:`, error);
    return false;
  }
};

/**
 * Clear all connections by removing all connection-related data
 * @returns {Promise<boolean>} True if successful
 */
export const clearConnections = async () => {
  try {
    // Get all keys from localStorage
    const allKeys = Object.keys(localStorage);

    // Filter keys by prefix to find all connection-related data (including manual contacts)
    const connectionKeys = allKeys.filter(
      (key) =>
        key.startsWith(CONNECTION_METADATA_PREFIX) ||
        key.startsWith(RECEIVED_PROFILE_DATA_PREFIX) ||
        key.startsWith(RECEIVED_ANON_DATA_PREFIX) ||
        key.startsWith(PROFILE_NOTES_PREFIX) ||
        key.startsWith(ANON_NOTES_PREFIX) ||
        key.startsWith(MANUAL_CONTACT_DATA_PREFIX)
    );

    // Delete all connection-related keys
    const promises = connectionKeys.map((key) => removeItem(key));
    await Promise.all(promises);

    // Also remove the legacy connections key and timestamp key
    await removeItem(CONNECTIONS_KEY);
    await removeItem(LAST_CONNECTION_TIMESTAMP_KEY);

    return true;
  } catch (error) {
    console.error('[STORAGE] Error clearing connections:', error);
    return false;
  }
};

/**
 * Get the timestamp of the last connection update
 * @returns {Promise<number|null>} Timestamp or null if not found
 */
export const getLastConnectionTimestamp = async () => {
  try {
    const timestamp = await getItem(LAST_CONNECTION_TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  } catch (error) {
    console.error('[STORAGE] Error getting last connection timestamp:', error);
    return null;
  }
};
